import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';
import { useLife, type RelationshipId } from './life';
import { detectTopic, NPC_TOPIC_REPLIES, GENERIC_TOPIC_REPLIES } from './replyContext';
import { getDialogueTree } from '@/content/dialogue';

export interface PhoneMessage {
  id: string;
  threadId: string;     // contact id
  fromSelf: boolean;
  fromNpc?: string;     // contact id when not self (denormalized for clarity)
  text: string;
  timestamp: number;
  read: boolean;
  actions?: PhoneAction[];   // inline CTAs that fire side effects when tapped
  actionsTakenIds?: string[];  // ids of actions already invoked (locks them)
}

export interface PhoneAction {
  id: string;
  label: string;
  effectId: string;     // dispatched to phoneActions.ts handler
  destructive?: boolean; // styles button as warning
}

export interface PhoneContact {
  id: string;
  displayName: string;
}

interface PhoneStore {
  contacts: Record<string, PhoneContact>;
  messages: PhoneMessage[];
  activeThreadId: string | null;
  receiveMessage: (m: { threadId: string; fromNpc: string; text: string; actions?: PhoneAction[] }) => void;
  sendMessage: (threadId: string, text: string) => void;
  openThread: (id: string) => void;
  closeThread: () => void;
  markActionTaken: (messageId: string, actionId: string) => void;
}

const SEED_CONTACTS: Record<string, PhoneContact> = {
  wife: { id: 'wife', displayName: 'Wife' },
  broker: { id: 'broker', displayName: 'Broker' },
  mom: { id: 'mom', displayName: 'Mom' },
  dad: { id: 'dad', displayName: 'Dad' },
  sister: { id: 'sister', displayName: 'Sister' },
  best_friend: { id: 'best_friend', displayName: 'Best Friend' },
};

const SEED_MESSAGES: PhoneMessage[] = [
  {
    id: nanoid(),
    threadId: 'broker',
    fromSelf: false,
    fromNpc: 'broker',
    text: "Welcome to RobbingTheHood Inc. Don't @ me when you're broke.",
    timestamp: Date.now() - 90_000,
    read: false,
  },
  {
    id: nanoid(),
    threadId: 'wife',
    fromSelf: false,
    fromNpc: 'wife',
    text: 'hey are you trading again',
    timestamp: Date.now() - 30_000,
    read: false,
  },
  {
    id: nanoid(),
    threadId: 'mom',
    fromSelf: false,
    fromNpc: 'mom',
    text: 'i saw something on the news. is that you?',
    timestamp: Date.now() - 10_000,
    read: false,
  },
  {
    id: nanoid(),
    threadId: 'dad',
    fromSelf: false,
    fromNpc: 'dad',
    text: 'son.\n\ncall your mother.\n\ndad',
    timestamp: Date.now() - 5_000,
    read: false,
  },
  {
    id: nanoid(),
    threadId: 'best_friend',
    fromSelf: false,
    fromNpc: 'best_friend',
    text: 'bro you alive',
    timestamp: Date.now() - 60_000,
    read: false,
  },
  {
    id: nanoid(),
    threadId: 'sister',
    fromSelf: false,
    fromNpc: 'sister',
    text: "engaged. mom said she\'d tell you. not waiting on it.",
    timestamp: Date.now() - 120_000,
    read: false,
  },
  // Older context seeds — give every thread some history so the player feels mid-conversation
  {
    id: nanoid(),
    threadId: 'wife',
    fromSelf: false,
    fromNpc: 'wife',
    text: "did you eat",
    timestamp: Date.now() - 3 * 3600_000,
    read: true,
  },
  {
    id: nanoid(),
    threadId: 'wife',
    fromSelf: true,
    text: "yeah",
    timestamp: Date.now() - 3 * 3600_000 + 60_000,
    read: true,
  },
  {
    id: nanoid(),
    threadId: 'wife',
    fromSelf: false,
    fromNpc: 'wife',
    text: "what.",
    timestamp: Date.now() - 3 * 3600_000 + 120_000,
    read: true,
  },
  {
    id: nanoid(),
    threadId: 'mom',
    fromSelf: false,
    fromNpc: 'mom',
    text: "your father wanted me to ask if your computer is plugged in. love mom.",
    timestamp: Date.now() - 5 * 3600_000,
    read: true,
  },
  {
    id: nanoid(),
    threadId: 'broker',
    fromSelf: false,
    fromNpc: 'broker',
    text: "fyi the SEC sent us a routine letter about you. routine. their words. they\'re not routine. the letter is not routine.",
    timestamp: Date.now() - 2 * 3600_000,
    read: true,
  },
];

function applyReplySideEffects(threadId: string): void {
  const life = useLife.getState();
  const lifeIdMap: Record<string, RelationshipId | undefined> = {
    wife: 'wife',
    mom: 'mom',
    broker: 'broker',
    lawyer: 'lawyer',
    best_friend: 'best_friend',
  };
  const lifeId = lifeIdMap[threadId];
  if (lifeId) {
    // Replying matters. Engagement is the cheapest currency.
    life.bumpRelationship(lifeId, 4);
  }

  // Contacts with an authored dialogue tree handle their own NPC reply via the
  // Conversation UI. Skip the legacy keyword/sentiment path so we don't
  // double-fire on the same player message.
  if (getDialogueTree(threadId)) return;

  // Legacy contextual follow-up for un-authored contacts (still better than silence).
  scheduleNpcFollowup(threadId);
}

const NPC_FOLLOWUPS: Record<string, { positive: string[]; negative: string[]; neutral: string[] }> = {
  wife: {
    positive: [
      "okay. that\'s nice. i\'m still mad. but. nice.",
      "i didn\'t expect that. unclear what to do with it.",
      "you can be sweet when you want to be. be that more.",
      "you remember things. that means a lot.",
    ],
    negative: [
      "we\'re done talking about this.",
      "okay. i\'m calling my lawyer.",
      "this is exactly what i\'m talking about.",
    ],
    neutral: [
      "k.",
      "...",
      "we\'ll talk about it later.",
      "did you eat.",
    ],
  },
  mom: {
    positive: [
      "love you sweetie ❤️",
      "see, that\'s the boy i raised.",
      "thank you for telling me. now please call your sister.",
      "you\'re a good boy. when you remember to be.",
      "thank you sweetie. that meant a lot.",
    ],
    negative: [
      "don\'t talk to your mother that way.",
      "i raised you better than this.",
      "your father is going to hear about this.",
      "i\'m going to pretend you didn\'t say that.",
    ],
    neutral: [
      "okay sweetie. the brisket is in the freezer.",
      "did you eat today? please eat.",
      "your sister called. she said hi. call her back.",
      "what time is it where you are? are you sleeping enough?",
      "ok sweetie. love you. tell me about your day when you can.",
    ],
  },
  best_friend: {
    positive: [
      "bro 🙌",
      "respect. let\'s grab beers. on you, allegedly.",
      "love that energy.",
    ],
    negative: [
      "bro. wow. okay.",
      "that was uncalled for. i\'ll allow it.",
    ],
    neutral: [
      "lol",
      "k bro",
      "send me alpha when you have it.",
    ],
  },
  broker: {
    positive: [
      "noted. i will pretend to act on this.",
      "thank you. you\'re still my favorite small account.",
      "appreciate the energy. doesn\'t change anything.",
    ],
    negative: [
      "i hear you. i am required to. it does not change anything.",
      "please put that in writing. via email. via your lawyer.",
      "we are not going to discuss this over text.",
    ],
    neutral: [
      "ack.",
      "received. logged. ignored, possibly.",
      "k. anything else from compliance?",
      "fyi the SEC also reads these. allegedly.",
    ],
  },
  lawyer: {
    positive: [
      "noted. i\'m still billing for this read.",
      "thank you. invoice forthcoming.",
    ],
    negative: [
      "you can\'t fire me by text. you have to email. i\'ll bill for the email.",
      "this is exactly the kind of message that becomes evidence.",
      "stop texting. start paying.",
    ],
    neutral: [
      "$1,400/h.",
      "received. billed.",
      "i charge per character. this one was free. the next one won\'t be.",
      "do not text me when you\'re drinking.",
    ],
  },
  'saudi-prince': {
    positive: [
      "we are pleased. the relationship endures.",
      "you understand the arrangement. this pleases me.",
      "your loyalty is, like the desert, vast.",
    ],
    negative: [
      "this is, unfortunately, not how we speak to one another.",
      "consider this a final, gentle, very gentle, warning.",
      "i had thought we were friends. i had thought.",
    ],
    neutral: [
      "i think of you often.",
      "the wires went out. as agreed.",
      "the falcon hunts. and so do we.",
      "when you visit, the hospitality will be considerable.",
    ],
  },
  senator: {
    positive: [
      "always good to hear from a constituent. especially a generous one.",
      "your support is noted. and counted.",
      "the door is open. the door is rarely open.",
    ],
    negative: [
      "i\'ll forget you said that. for now.",
      "we don\'t talk like that on official phones. or any phones.",
      "i\'ll remember. i remember everything.",
    ],
    neutral: [
      "received. shredding now.",
      "we\'ll talk in person. ideally not in dc.",
      "i don\'t answer texts. someone is doing this for me.",
      "thanks for thinking of the campaign.",
    ],
  },
  intel: {
    positive: [
      "good. continue.",
      "your discretion is appreciated. and required.",
      "the channel remains open.",
    ],
    negative: [
      "this number will be reassigned within the hour.",
      "we will not be in touch. probably.",
      "consider the engagement concluded.",
    ],
    neutral: [
      "acknowledged.",
      "•••",
      "the package is en route. or it is not.",
      "you will not hear from us again. unless you do.",
    ],
  },
  reporter: {
    positive: [
      "appreciate you. on the record or off?",
      "thanks. i\'ll handle it carefully. probably.",
      "screenshotting this for the piece.",
    ],
    negative: [
      "i can already see the headline. it writes itself.",
      "deadline is in 40 minutes. now i\'m REALLY writing it.",
      "you\'re the third person to text me \"no comment\" in 4 minutes.",
    ],
    neutral: [
      "got it. will reach out for confirmation. you will regret giving me your number.",
      "off the record? sure. that means \"on the record but i\'ll feel bad about it.\"",
      "noted. cite as \"a person familiar with the matter.\"",
    ],
  },
};

// Fallback pool — fires for any contact not in the table above so the player
// always gets *something* back. Avoids the dead-feeling silence the user flagged.
const GENERIC_FOLLOWUPS = {
  positive: [
    "appreciate it.",
    "noted. thank you.",
    "ok. that\'s good.",
    "kind of you to say.",
  ],
  negative: [
    "ok. let\'s talk later. or not.",
    "received. not engaging with that.",
    "noted. not a fan.",
  ],
  neutral: [
    "k.",
    "got it.",
    "ack.",
    "we\'ll see.",
    "ok.",
  ],
};

function scheduleNpcFollowup(threadId: string): void {
  const followups = NPC_FOLLOWUPS[threadId] ?? GENERIC_FOLLOWUPS;
  const lastMsg = usePhone.getState().messages.filter((m) => m.threadId === threadId && m.fromSelf).slice(-1)[0];
  if (!lastMsg) return;
  const text = lastMsg.text;
  const lc = text.toLowerCase();

  // Topic-aware reply: scan player text for keyword clusters (SEC, margin,
  // money, divorce, food, …) and try to pull from a topic-keyed pool first.
  // Without this, the system was sentiment-only and felt like a magic 8-ball.
  let reply: string | undefined;
  const topicMatch = detectTopic(text);
  if (topicMatch) {
    const npcTopicPool = NPC_TOPIC_REPLIES[threadId]?.[topicMatch.topic];
    const genericTopicPool = GENERIC_TOPIC_REPLIES[topicMatch.topic];
    const pool = (npcTopicPool && npcTopicPool.length > 0) ? npcTopicPool : genericTopicPool;
    if (pool && pool.length > 0) {
      reply = pool[Math.floor(Math.random() * pool.length)];
    }
  }
  // Sentiment fallback if no topic match (or topic-pool was empty).
  if (!reply) {
    let pool: string[];
    if (/\bsorry\b|\blove\b|\bplease\b|\bi miss\b|\bbeautiful\b|\bthank/i.test(lc)) {
      pool = followups.positive;
    } else if (/\bno\b|\bstop\b|\bfuck\b|\bidiot\b|\bshut\b|\blater\b|\bhate\b|\bnever\b/i.test(lc)) {
      pool = followups.negative;
    } else {
      pool = followups.neutral;
    }
    reply = pool[Math.floor(Math.random() * pool.length)];
  }

  // ~85% chance to follow up. Previously 30% which read as "they don't reply".
  if (Math.random() > 0.85) return;
  if (!reply) return;
  // 6-18s delay — feels like a conversation, not a lost SMS from 2009.
  setTimeout(() => {
    usePhone.getState().receiveMessage({
      threadId,
      fromNpc: threadId,
      text: reply,
    });
  }, 6_000 + Math.floor(Math.random() * 12_000));
}

export const usePhone = create<PhoneStore>()(
  immer((set) => ({
    contacts: SEED_CONTACTS,
    messages: SEED_MESSAGES,
    activeThreadId: null,
    receiveMessage: ({ threadId, fromNpc, text, actions }) => set((s) => {
      s.messages.push({
        id: nanoid(),
        threadId,
        fromSelf: false,
        fromNpc,
        text,
        timestamp: Date.now(),
        read: false,
        actions,
        actionsTakenIds: [],
      });
    }),
    sendMessage: (threadId, text) => {
      set((s) => {
        s.messages.push({
          id: nanoid(),
          threadId,
          fromSelf: true,
          text,
          timestamp: Date.now(),
          read: true,
        });
      });
      // Cross-store side effect: replying to wife/mom/friend bumps relationship.
      queueMicrotask(() => applyReplySideEffects(threadId));
    },
    openThread: (id) => set((s) => {
      s.activeThreadId = id;
      for (const m of s.messages) {
        if (m.threadId === id && !m.fromSelf) m.read = true;
      }
    }),
    closeThread: () => set((s) => { s.activeThreadId = null; }),
    markActionTaken: (messageId, actionId) => set((s) => {
      const m = s.messages.find((x) => x.id === messageId);
      if (!m) return;
      if (!m.actionsTakenIds) m.actionsTakenIds = [];
      if (!m.actionsTakenIds.includes(actionId)) m.actionsTakenIds.push(actionId);
    }),
  })),
);
